import type { AuthApiError, AuthValidationErrorItem, AuthErrorInfo } from "../types";
import { AuthError as AuthErrorClass } from "../types";

/**
 * The API mutation hooks throw the parsed response body itself
 * (`{ message, code, statusCode }`), with no `body` wrapper around it.
 */
interface PlainErrorBody {
  message: unknown;
  code?: unknown;
  statusCode?: unknown;
}

function readCode(error: PlainErrorBody): string | undefined {
  return typeof error.code === "string" ? error.code : undefined;
}

function readStatus(error: PlainErrorBody): number | undefined {
  return typeof error.statusCode === "number" ? error.statusCode : undefined;
}

export function createAuthErrorInfo(
  error: unknown,
  fallbackMessage: string,
  defaultCode: string = "UNKNOWN_ERROR",
): AuthErrorInfo {
  try {
    // Check if it's already an AuthError
    if (error instanceof AuthErrorClass) {
      return {
        message: error.message,
        code: error.code,
        status: error.status,
      };
    }

    if (error && typeof error === "object" && "body" in error) {
      const apiError = error as AuthApiError;

      if (apiError.body?.message) {
        const message = apiError.body.message;

        if (typeof message === "string") {
          return {
            message,
            code: apiError.body.code || defaultCode,
            status: apiError.status || apiError.body.statusCode,
          };
        }

        if (Array.isArray(message)) {
          const validationErrors = message as AuthValidationErrorItem[];
          if (validationErrors.length > 0 && validationErrors[0].message) {
            return {
              message: validationErrors[0].message,
              code: "VALIDATION_ERROR",
              status: apiError.status || apiError.body.statusCode,
            };
          }
        }

        if (message && typeof message === "object") {
          const messageObj = message as Record<string, unknown>;
          if ("message" in messageObj && typeof messageObj.message === "string") {
            return {
              message: messageObj.message,
              code: apiError.body.code || defaultCode,
              status: apiError.status || apiError.body.statusCode,
            };
          }
        }

        // Convert any other type to string
        return {
          message: String(message),
          code: apiError.body.code || defaultCode,
          status: apiError.status || apiError.body.statusCode,
        };
      }

      // Fallback to error property if no message
      if (apiError.body?.error) {
        return {
          message: apiError.body.error,
          code: apiError.body.code || defaultCode,
          status: apiError.status || apiError.body.statusCode,
        };
      }
    }

    if (error instanceof Error) {
      return {
        message: error.message,
        code: defaultCode,
      };
    }

    if (error && typeof error === "object" && "message" in error) {
      const errorObj = error as PlainErrorBody;
      if (typeof errorObj.message === "string") {
        return {
          message: errorObj.message,
          code: readCode(errorObj) ?? defaultCode,
          status: readStatus(errorObj),
        };
      }
    }
  } catch (parseError) {
    console.warn("Error parsing error for AuthErrorInfo creation:", parseError);
  }

  return {
    message: fallbackMessage,
    code: defaultCode,
  };
}

export function createAuthError(
  error: unknown,
  fallbackMessage: string,
  defaultCode: string = "UNKNOWN_ERROR",
): AuthErrorClass {
  try {
    // Check if it's already an AuthError
    if (error instanceof AuthErrorClass) {
      return error;
    }

    if (error && typeof error === "object" && "body" in error) {
      const apiError = error as AuthApiError;

      if (apiError.body?.message) {
        const message = apiError.body.message;

        if (typeof message === "string") {
          return new AuthErrorClass(
            message,
            apiError.body.code || defaultCode,
            apiError.body.details,
            apiError.status || apiError.body.statusCode,
          );
        }

        if (Array.isArray(message)) {
          const validationErrors = message as AuthValidationErrorItem[];
          if (validationErrors.length > 0 && validationErrors[0].message) {
            return new AuthErrorClass(
              validationErrors[0].message,
              "VALIDATION_ERROR",
              { validationErrors, property: validationErrors[0].property },
              apiError.status || apiError.body.statusCode,
            );
          }
        }

        if (message && typeof message === "object") {
          const messageObj = message as Record<string, unknown>;
          if ("message" in messageObj && typeof messageObj.message === "string") {
            return new AuthErrorClass(
              messageObj.message,
              apiError.body.code || defaultCode,
              apiError.body.details,
              apiError.status || apiError.body.statusCode,
            );
          }
        }

        // Convert any other type to string
        return new AuthErrorClass(
          String(message),
          apiError.body.code || defaultCode,
          apiError.body.details,
          apiError.status || apiError.body.statusCode,
        );
      }

      // Fallback to error property if no message
      if (apiError.body?.error) {
        return new AuthErrorClass(
          apiError.body.error,
          apiError.body.code || defaultCode,
          apiError.body.details,
          apiError.status || apiError.body.statusCode,
        );
      }
    }

    if (error instanceof Error) {
      return new AuthErrorClass(error.message, defaultCode, { originalError: error });
    }

    if (error && typeof error === "object" && "message" in error) {
      const errorObj = error as PlainErrorBody;
      if (typeof errorObj.message === "string") {
        return new AuthErrorClass(
          errorObj.message,
          readCode(errorObj) ?? defaultCode,
          { originalError: error },
          readStatus(errorObj),
        );
      }
    }
  } catch (parseError) {
    console.warn("Error parsing error for AuthError creation:", parseError);
  }

  return new AuthErrorClass(fallbackMessage, defaultCode, { originalError: error });
}

export function getErrorStatus(error: unknown): number | null {
  // Check multiple possible locations for status code
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    return (err.status as number) ||
           (err.statusCode as number) ||
           ((err.response as Record<string, unknown>)?.status as number) ||
           ((err.response as Record<string, unknown>)?.statusCode as number) ||
           null;
  }
  return null;
}

/**
 * Checks if an error is a network error (no HTTP status or server error).
 * Network errors include:
 * - No status code (fetch failed, connection refused, etc.)
 * - 5xx server errors (API is down or misconfigured)
 */
export function isNetworkError(error: unknown): boolean {
  const status = getErrorStatus(error);

  // No status = network error (fetch failed, connection refused)
  if (status === null) return true;

  // 5xx = server error, not an authentication issue
  if (status >= 500) return true;

  return false;
}

/**
 * Determines if a user should be disconnected based on the error.
 * Only returns true for authentication errors (401/403).
 * Network errors and server errors do not trigger disconnection.
 */
export function shouldDisconnectUser(error: unknown): boolean {
  const status = getErrorStatus(error);

  // 401/403 = authentication error, user should be disconnected
  return status === 401 || status === 403;
}
